import { csrfHeaders } from './csrf';

const SHARE_INDISPONIVEL = {
  titulo: 'Este link não está mais disponível',
  corpo: 'O acesso expirou, foi revogado ou o endereço não existe.',
  proximo_passo: 'Peça um novo link a quem compartilhou a nota.',
} as const;

const SHARE_FALHOU = {
  titulo: 'Não foi possível compartilhar a nota',
  corpo: 'O sistema não escolhe contato e não envia sozinho.',
  proximo_passo: 'Copie o link e envie pelo WhatsApp ou pelo aplicativo que preferir.',
} as const;

type AvisoShare = {
  titulo: string;
  corpo: string;
  proximo_passo: string;
};

let bound = false;

function isAbort(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

function feedbackHost(trigger: HTMLElement): HTMLElement {
  const existing = trigger.parentElement?.querySelector<HTMLElement>('[data-share-feedback]');
  if (existing) {
    return existing;
  }
  const host = document.createElement('div');
  host.setAttribute('data-share-feedback', '');
  host.setAttribute('role', 'status');
  host.setAttribute('aria-live', 'polite');
  trigger.insertAdjacentElement('afterend', host);
  return host;
}

function mostrar(trigger: HTMLElement, aviso: AvisoShare, extra?: string): void {
  const host = feedbackHost(trigger);
  host.hidden = false;
  host.replaceChildren();
  const title = document.createElement('strong');
  title.textContent = aviso.titulo;
  const corpo = document.createElement('p');
  corpo.textContent = aviso.corpo;
  const proximo = document.createElement('p');
  proximo.textContent = extra && extra !== '' ? extra : aviso.proximo_passo;
  host.append(title, corpo, proximo);
}

type ShareCreatePayload = {
  url?: string;
  pdf_url?: string;
  titulo?: string;
  corpo?: string;
  proximo_passo?: string;
  correlation_id?: string;
};

const SHARE_CRIACAO_FALHOU = {
  titulo: 'Compartilhamento indisponível agora',
  corpo: 'Não confirmamos a criação do link. A nota e seus arquivos permanecem guardados.',
  proximo_passo: 'Aguarde e tente novamente. Informe o código exibido se continuar.',
} as const;

async function criarECompartilhar(trigger: HTMLElement): Promise<void> {
  const form = trigger.closest<HTMLFormElement>('form[data-share-create]');
  if (!form) {
    mostrar(trigger, SHARE_CRIACAO_FALHOU);
    return;
  }
  trigger.setAttribute('aria-busy', 'true');
  if (trigger instanceof HTMLButtonElement) {
    trigger.disabled = true;
  }
  let response: Response;
  try {
    response = await fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
      headers: csrfHeaders({ Accept: 'application/json' }),
    });
  } catch (error) {
    void error;
    mostrar(trigger, SHARE_CRIACAO_FALHOU);
    trigger.removeAttribute('aria-busy');
    if (trigger instanceof HTMLButtonElement) {
      trigger.disabled = false;
    }
    return;
  }
  let payload: ShareCreatePayload = {};
  try {
    payload = (await response.json()) as ShareCreatePayload;
  } catch (error) {
    void error;
  }
  if (!response.ok || !payload.url) {
    mostrar(trigger, {
      titulo: payload.titulo || SHARE_CRIACAO_FALHOU.titulo,
      corpo: payload.corpo || SHARE_CRIACAO_FALHOU.corpo,
      proximo_passo: payload.proximo_passo || SHARE_CRIACAO_FALHOU.proximo_passo,
    }, payload.correlation_id ? `${payload.proximo_passo || SHARE_CRIACAO_FALHOU.proximo_passo} Código de correlação: ${payload.correlation_id}` : undefined);
    trigger.removeAttribute('aria-busy');
    if (trigger instanceof HTMLButtonElement) {
      trigger.disabled = false;
    }
    return;
  }
  trigger.removeAttribute('data-share-create');
  form.removeAttribute('data-share-create');
  trigger.setAttribute('data-share', '');
  trigger.setAttribute('data-share-url', payload.url);
  trigger.setAttribute('data-share-title', `NFS-e ${form.action.split('/').at(-2) || ''}`.trim());
  trigger.setAttribute('data-share-text', 'Segue a NFS-e');
  if (payload.pdf_url) {
    trigger.setAttribute('data-share-pdf', payload.pdf_url);
    trigger.setAttribute('data-share-filename', 'DANFSe.pdf');
  }
  trigger.removeAttribute('aria-busy');
  if (trigger instanceof HTMLButtonElement) {
    trigger.disabled = false;
    trigger.textContent = 'Escolher aplicativo';
  }
  mostrar(trigger, {
    titulo: payload.titulo || 'Link de leitura criado',
    corpo: payload.corpo || 'Nenhum contato recebeu a nota automaticamente.',
    proximo_passo: payload.proximo_passo || 'Escolha o aplicativo ou copie o link para enviar.',
  });
  await shareFromTrigger(trigger);
}

function whatsAppUrl(text: string, url: string): string {
  const message = text.trim() === '' ? url : `${text.trim()}\n${url}`;
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

function canSharePayload(data: ShareData): boolean {
  if (typeof navigator.share !== 'function') {
    return false;
  }
  if (typeof navigator.canShare !== 'function') {
    return data.files === undefined;
  }
  try {
    return navigator.canShare(data);
  } catch (error) {
    void error;
    return false;
  }
}

async function copiar(texto: string): Promise<boolean> {
  if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
    return false;
  }
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch (error) {
    void error;
    return false;
  }
}

async function baixarPdf(
  pdfUrl: string,
): Promise<{ kind: 'ok'; file: File } | { kind: 'indisponivel' } | { kind: 'sem-arquivo' }> {
  try {
    const response = await fetch(pdfUrl, {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store',
    });
    if (response.status === 404 || response.status === 410 || response.status === 403) {
      return { kind: 'indisponivel' };
    }
    if (!response.ok) {
      return { kind: 'sem-arquivo' };
    }
    const type = response.headers.get('content-type') ?? '';
    if (type.includes('xml') || type.includes('pkcs12')) {
      return { kind: 'sem-arquivo' };
    }
    const blob = await response.blob();
    if (blob.size === 0) {
      return { kind: 'sem-arquivo' };
    }
    const nameFromHeader = filenameFromDisposition(response.headers.get('content-disposition'));
    const file = new File([blob], nameFromHeader ?? 'DANFSe.pdf', {
      type: blob.type || 'application/pdf',
    });
    return { kind: 'ok', file };
  } catch (error) {
    if (isAbort(error)) {
      return { kind: 'sem-arquivo' };
    }
    return { kind: 'sem-arquivo' };
  }
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) {
    return null;
  }
  const match = /filename\*?=(?:UTF-8''|"?)([^";]+)/iu.exec(header);
  const raw = match?.[1];
  if (!raw) {
    return null;
  }
  try {
    return decodeURIComponent(raw.replace(/"/gu, '')).trim();
  } catch (error) {
    void error;
    return raw.replace(/"/gu, '').trim();
  }
}

function abrirWhatsApp(text: string, url: string): boolean {
  const target = whatsAppUrl(text, url);
  const opened = window.open(target, '_blank', 'noopener,noreferrer');
  return opened !== null;
}

export async function shareFromTrigger(trigger: HTMLElement): Promise<void> {
  const url = trigger.getAttribute('data-share-url')?.trim() ?? '';
  const title = trigger.getAttribute('data-share-title')?.trim() || 'NFS-e';
  const text = trigger.getAttribute('data-share-text')?.trim() || 'Segue a NFS-e';
  const pdfUrl = trigger.getAttribute('data-share-pdf')?.trim() ?? '';
  const pdfName = trigger.getAttribute('data-share-filename')?.trim() || 'DANFSe.pdf';

  if (url === '') {
    mostrar(trigger, SHARE_INDISPONIVEL);
    return;
  }

  let file: File | undefined;
  if (pdfUrl !== '') {
    const pdf = await baixarPdf(pdfUrl);
    if (pdf.kind === 'indisponivel') {
      mostrar(trigger, SHARE_INDISPONIVEL);
      return;
    }
    if (pdf.kind === 'ok') {
      file = new File([pdf.file], pdfName, { type: pdf.file.type });
    }
  }

  const withFile: ShareData = file ? { title, text, url, files: [file] } : { title, text, url };
  const withoutFile: ShareData = { title, text, url };

  try {
    if (file && canSharePayload(withFile)) {
      await navigator.share(withFile);
      return;
    }
    if (canSharePayload(withoutFile)) {
      await navigator.share(withoutFile);
      return;
    }
  } catch (error) {
    if (isAbort(error)) {
      return;
    }
  }

  if (abrirWhatsApp(text, url)) {
    return;
  }
  const copiou = await copiar(`${text}\n${url}`);
  mostrar(
    trigger,
    SHARE_FALHOU,
    copiou
      ? 'O link foi copiado. Cole no WhatsApp ou no aplicativo que preferir.'
      : `${SHARE_FALHOU.proximo_passo} ${url}`,
  );
}

export function bindShare(): void {
  if (bound) {
    return;
  }
  bound = true;
  document.addEventListener('click', (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const createTrigger = target.closest('[data-share-create] button[type="submit"]');
    if (createTrigger instanceof HTMLElement) {
      event.preventDefault();
      void criarECompartilhar(createTrigger);
      return;
    }
    const trigger = target.closest('[data-share]');
    if (!(trigger instanceof HTMLElement)) {
      return;
    }
    event.preventDefault();
    void shareFromTrigger(trigger);
  });
}
